import { PublicServiceCategory, Service } from '../types';

/** Typed 409 detail codes the admin category endpoints return. */
export const SERVICE_CATEGORY_CONFLICT = {
  duplicateSlug: 'SERVICE_CATEGORY_DUPLICATE_SLUG',
  duplicateLabel: 'SERVICE_CATEGORY_DUPLICATE_LABEL',
  inUse: 'SERVICE_CATEGORY_IN_USE',
} as const;

export type ServiceCategoryConflictCode =
  (typeof SERVICE_CATEGORY_CONFLICT)[keyof typeof SERVICE_CATEGORY_CONFLICT];

/**
 * Sentinel filter value emitted by the "More" chip. Services whose category
 * is null, undefined, or in an inactive category map to this bucket. Using a named
 * constant avoids scattering the string literal across multiple call sites.
 */
export const MORE_CATEGORY_VALUE = 'more';

/**
 * Display label for the trailing null/unknown-category bucket. Used in
 * section headings and chip labels.
 */
export const MORE_BUCKET_LABEL = 'More';

export interface ServiceCategoryBucket {
  /** Category slug, or MORE_CATEGORY_VALUE for the trailing bucket. */
  category: string;
  heading: string;
  items: Service[];
}

/** The label for a service's category when that category is active, else null. */
export function publicCategoryLabel(service: Service): string | null {
  const detail = service.category_detail;
  return detail?.is_active ? detail.label : null;
}

/**
 * Groups services into one bucket per category in the given (admin) order, then a
 * trailing More bucket for services with no category or one outside the list.
 */
export function groupServicesByCategory(
  services: Service[],
  categories: PublicServiceCategory[],
): ServiceCategoryBucket[] {
  const buckets = categories.flatMap(({ slug, label }) => {
    const items = services.filter((service) => service.category === slug);
    return items.length > 0 ? [{ category: slug, heading: label, items }] : [];
  });
  const knownSlugs = new Set(categories.map(({ slug }) => slug));
  const moreItems = services.filter(
    (service) => !service.category || !knownSlugs.has(service.category),
  );
  return moreItems.length > 0
    ? [...buckets, { category: MORE_CATEGORY_VALUE, heading: MORE_BUCKET_LABEL, items: moreItems }]
    : buckets;
}

const CONFLICT_CODES: readonly string[] = Object.values(SERVICE_CATEGORY_CONFLICT);

/** The typed conflict code in a 409 response detail, or null for any other response. */
export function conflictCodeFromResponse(
  status: number | null,
  detail: unknown,
): ServiceCategoryConflictCode | null {
  if (status !== 409 || typeof detail !== 'object' || detail === null) return null;
  const { code } = detail as { code?: unknown };
  return typeof code === 'string' && CONFLICT_CODES.includes(code)
    ? (code as ServiceCategoryConflictCode)
    : null;
}

/** A URL slug proposed from a label; the admin can edit it before creating. */
export function deriveCategorySlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const SLUG_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const SLUG_MAX_LENGTH = 60;
const VALUE_ERROR_PREFIX = /^Value error, /;

/** Why a slug cannot be created, or null when it is acceptable (an empty slug is not judged here). */
export function slugProblem(slug: string): string | null {
  if (slug === '') return null;
  if (slug === MORE_CATEGORY_VALUE) {
    return `The slug "${MORE_CATEGORY_VALUE}" is reserved. Choose a different one.`;
  }
  if (slug.length > SLUG_MAX_LENGTH) return `Slug must be ${SLUG_MAX_LENGTH} characters or fewer.`;
  if (!SLUG_PATTERN.test(slug)) {
    return 'Use lowercase letters and digits, separated by single hyphens or underscores.';
  }
  return null;
}

/** The backend's explanation of a 422: a string detail, or the first validation error's message. */
export function unprocessableMessage(status: number | null, detail: unknown): string | null {
  if (status !== 422) return null;
  if (typeof detail === 'string') return detail === '' ? null : detail;
  if (!Array.isArray(detail)) return null;
  for (const entry of detail) {
    const message = (entry as { msg?: unknown } | null)?.msg;
    if (typeof message === 'string' && message !== '') {
      return message.replace(VALUE_ERROR_PREFIX, '');
    }
  }
  return null;
}
